import React, { useState, useEffect } from 'react';
import { Layout, Card, Table, Button, Modal, Form, Input, Select, message, Space, Typography, Tag, Statistic, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, ReloadOutlined, BankOutlined } from '@ant-design/icons';
import api from '../../utils/api';

const { Title, Text } = Typography;
const { Option } = Select;

const BankAccountsPage = () => {
    const [bankAccounts, setBankAccounts] = useState([]);
    const [glAccounts, setGlAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchBankAccounts();
        fetchGlAccounts();
    }, []);

    const fetchBankAccounts = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/accounting/bank-accounts');
            if (res.data.success) {
                setBankAccounts(res.data.data);
            }
        } catch (error) {
            message.error('ไม่สามารถดึงข้อมูลบัญชีธนาคารได้');
        } finally {
            setLoading(false);
        }
    };

    const fetchGlAccounts = async () => {
        try {
            const res = await api.get('/api/accounting/accounts');
            if (res.data.success) {
                setGlAccounts(res.data.data.filter(a => a.type === 'asset'));
            }
        } catch (error) { }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingItem) {
                await api.put(`/api/accounting/bank-accounts/${editingItem.id}`, values);
                message.success('แก้ไขบัญชีธนาคารเรียบร้อย');
            } else {
                await api.post('/api/accounting/bank-accounts', values);
                message.success('เพิ่มบัญชีธนาคารเรียบร้อย');
            }
            setIsModalVisible(false);
            form.resetFields();
            setEditingItem(null);
            fetchBankAccounts();
        } catch (error) {
            message.error('ทำรายการไม่สำเร็จ');
        }
    };

    const columns = [
        {
            title: 'ชื่อบัญชี',
            dataIndex: 'account_name',
            key: 'account_name',
            render: (text) => <Space><BankOutlined /> {text}</Space>
        },
        { title: 'ธนาคาร', dataIndex: 'bank_name', key: 'bank_name' },
        { title: 'เลขที่บัญชี', dataIndex: 'account_number', key: 'account_number' },
        {
            title: 'ผังบัญชีที่เชื่อม',
            key: 'gl_account',
            render: (_, record) => {
                const code = record.gl_account_code || record.GL_ACCOUNT_CODE;
                const name = record.gl_account_name || record.GL_ACCOUNT_NAME;
                return code ? `${code} - ${name}` : '-';
            }
        },
        {
            title: 'ยอดคงเหลือ',
            dataIndex: 'balance',
            key: 'balance',
            align: 'right',
            render: (val) => <Text strong>{parseFloat(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>
        },
        {
            title: 'สถานะ',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (active) => <Tag color={active ? 'green' : 'red'}>{active ? 'ใช้งาน' : 'ระงับ'}</Tag>
        },
        {
            title: 'จัดการ',
            key: 'actions',
            render: (_, record) => (
                <Button icon={<EditOutlined />} size="small" onClick={() => {
                    setEditingItem(record);
                    form.setFieldsValue(record);
                    setIsModalVisible(true);
                }} />
            )
        }
    ];

    const totalBalance = bankAccounts.reduce((sum, acc) => sum + parseFloat(acc.balance || 0), 0);

    return (
        <Layout style={{ padding: '24px' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={2}>บัญชีธนาคารบริษัท (Company Bank Accounts)</Title>
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={fetchBankAccounts}>รีเฟรช</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                            setEditingItem(null);
                            form.resetFields();
                            setIsModalVisible(true);
                        }}>เพิ่มบัญชีธนาคาร</Button>
                    </Space>
                </div>

                <Row gutter={16}>
                    <Col span={8}>
                        <Card>
                            <Statistic
                                title="ยอดเงินรวมทุกบัญชี"
                                value={totalBalance}
                                precision={2}
                                suffix="บาท"
                                valueStyle={{ color: '#3f51b5' }}
                            />
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card>
                            <Statistic title="จำนวนบัญชีทั้งหมด" value={bankAccounts.length} suffix="บัญชี" />
                        </Card>
                    </Col>
                </Row>

                <Card>
                    <Table
                        columns={columns}
                        dataSource={bankAccounts}
                        rowKey="id"
                        loading={loading}
                    />
                </Card>
            </Space>

            <Modal
                title={editingItem ? "แก้ไขบัญชีธนาคาร" : "เพิ่มบัญชีธนาคารใหม่"}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                okText="บันทึก"
                cancelText="ยกเลิก"
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item name="account_name" label="ชื่อเรียกบัญชี" rules={[{ required: true, message: 'กรุณาระบุชื่อเรียก' }]}>
                        <Input placeholder="เช่น กสิกรไทย (AtomSoft)" />
                    </Form.Item>
                    <Form.Item name="bank_name" label="ธนาคาร" rules={[{ required: true, message: 'กรุณาระบุธนาคาร' }]}>
                        <Input placeholder="เช่น ธนาคารกสิกรไทย" />
                    </Form.Item>
                    <Form.Item name="account_number" label="เลขที่บัญชี" rules={[{ required: true, message: 'กรุณาระบุเลขที่บัญชี' }]}>
                        <Input placeholder="เช่น 123-4-56789-0" />
                    </Form.Item>
                    <Form.Item name="branch" label="สาขา">
                        <Input placeholder="เช่น สาขาเซ็นทรัลเวิลด์" />
                    </Form.Item>
                    <Form.Item name="gl_account_id" label="เชื่อมกับผังบัญชี" rules={[{ required: true, message: 'กรุณาเลือกผังบัญชี' }]}>
                        <Select placeholder="เลือกบัญชีแยกประเภท">
                            {glAccounts.map(acc => (
                                <Option key={acc.id} value={acc.id}>
                                    {(acc.CODE || acc.code)} - {(acc.NAME || acc.name)}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {editingItem && (
                        <Form.Item name="is_active" label="สถานะ" valuePropName="checked">
                            <Select>
                                <Option value={1}>ใช้งาน</Option>
                                <Option value={0}>ระงับ</Option>
                            </Select>
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </Layout>
    );
};

export default BankAccountsPage;
